import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type FormEvent,
  type ReactElement,
} from "react";

import { apiList, errMessage } from "../../api/client";
import type { Batch } from "../../api/types";

// The batch list + upload form + progress table shared by the collab Batches
// tab and the admin Batch Upload card — the two consoles must render the same
// batch UI, only the endpoints and the surrounding chrome differ.

// Load + poll a batch list. `path` null means "no target chosen yet" (the
// admin card before a protocol is picked): the list clears and nothing loads.
export function useBatchList(path: string | null): {
  batches: Batch[];
  loading: boolean;
  error: string | null;
  reload: () => Promise<void>;
} {
  const [batches, setBatches] = useState<Batch[]>([]);
  const [loading, setLoading] = useState(path !== null);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    if (!path) {
      setBatches([]);
      return;
    }
    setLoading(true);
    try {
      setBatches(await apiList<Batch>(path));
      setError(null);
    } catch (err) {
      setError(errMessage(err, "Could not load batches."));
    } finally {
      setLoading(false);
    }
  }, [path]);

  useEffect(() => {
    void reload();
  }, [reload]);

  // Poll while any batch still has photos pending inference.
  const anyPending = batches.some((b) => b.pending_photos > 0);
  useEffect(() => {
    if (!anyPending) return;
    const id = setInterval(() => void reload(), 4000);
    return () => clearInterval(id);
  }, [anyPending, reload]);

  return { batches, loading, error, reload };
}

// The name/manifest/photos form. `onUpload` throws on failure (the thrown
// ApiError's message is shown); on success the fields clear.
export function BatchUploadForm({
  busy,
  busyLabel,
  namePlaceholder,
  onUpload,
}: {
  busy: boolean;
  busyLabel?: string;
  namePlaceholder: string;
  onUpload: (name: string, manifest: File, photos: File[]) => Promise<void>;
}): ReactElement {
  const [name, setName] = useState("");
  const [manifest, setManifest] = useState<File | null>(null);
  const [photos, setPhotos] = useState<FileList | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const photosRef = useRef<HTMLInputElement>(null);
  const manifestRef = useRef<HTMLInputElement>(null);

  async function upload(e: FormEvent): Promise<void> {
    e.preventDefault();
    if (!manifest || !photos || photos.length === 0) return;
    setFormError(null);
    try {
      await onUpload(name, manifest, Array.from(photos));
      setName("");
      setManifest(null);
      setPhotos(null);
      if (photosRef.current) photosRef.current.value = "";
      if (manifestRef.current) manifestRef.current.value = "";
    } catch (err) {
      setFormError(errMessage(err, "Upload failed."));
    }
  }

  return (
    <form className="q-form" onSubmit={upload}>
      <label className="field">
        <span>Batch name</span>
        <input
          required
          maxLength={200}
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder={namePlaceholder}
        />
      </label>
      <label className="field">
        <span>Manifest CSV (columns: filename, email)</span>
        <input
          ref={manifestRef}
          type="file"
          accept=".csv,text/csv"
          required
          onChange={(e) => setManifest(e.target.files?.[0] ?? null)}
        />
      </label>
      <label className="field">
        <span>Photos</span>
        <input
          ref={photosRef}
          type="file"
          accept="image/jpeg,image/png,image/heic,image/heif"
          multiple
          required
          onChange={(e) => setPhotos(e.target.files)}
        />
      </label>
      {formError ? <p className="form-error">{formError}</p> : null}
      {/* Only disabled while busy — a disabled button swallows the click
          silently; enabled, the browser's `required` hints name the
          missing field on submit. */}
      <button className="btn-primary-glow" type="submit" disabled={busy}>
        {busy ? (busyLabel ?? "Uploading…") : "Upload batch"}
      </button>
    </form>
  );
}

// The 6-column progress table.
export function BatchTable({
  batches,
  onDownload,
}: {
  batches: Batch[];
  onDownload: (batch: Batch) => void;
}): ReactElement {
  return (
    <table className="data-table">
      <thead>
        <tr>
          <th>Name</th>
          <th>Photos</th>
          <th>Processed</th>
          <th>Pending</th>
          <th>Failed</th>
          <th>Results</th>
        </tr>
      </thead>
      <tbody>
        {batches.map((b) => (
          <tr key={b.id}>
            <td>{b.name}</td>
            <td>{b.total_photos}</td>
            <td>{b.processed_photos}</td>
            <td>{b.pending_photos}</td>
            <td>{b.failed_photos}</td>
            <td>
              <button className="btn-ghost btn-sm" onClick={() => onDownload(b)}>
                Download CSV
              </button>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
